import { analyticsManager } from '../analytics';

/**
 * Track referral source from URL parameters or referrer
 */
export const trackReferralSource = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm_source = params.get('utm_source');
    const utm_medium = params.get('utm_medium');
    const utm_campaign = params.get('utm_campaign');
    const referrer = document.referrer;

    let referralSource = 'direct';
    let referralDetails: any = {
      utm_source,
      utm_medium,
      utm_campaign,
      referrer_host: referrer ? new URL(referrer).hostname : 'direct',
    };

    if (utm_source || utm_medium || utm_campaign) {
      referralSource = 'utm';
    } else if (referrer && referrer !== '') {
      referralSource = 'organic';
    }

    analyticsManager.trackCustomEvent('referral_tracked', {
      referral_source: referralSource,
      ...referralDetails,
    });
  } catch (error) {
    console.error('Failed to track referral source:', error);
  }
};

/**
 * Track admin link click
 */
export const trackAdminLinkClick = (linkType: string, linkTarget: string) => {
  analyticsManager.trackCustomEvent('admin_link_clicked', {
    link_type: linkType,
    link_target: linkTarget,
  });
};

/**
 * Track UTM parameters on page load
 */
export const trackUTMParameters = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm_source = params.get('utm_source');
    const utm_medium = params.get('utm_medium');
    const utm_campaign = params.get('utm_campaign');
    const utm_content = params.get('utm_content');
    const utm_term = params.get('utm_term');

    if (utm_source || utm_medium || utm_campaign) {
      analyticsManager.trackCustomEvent('utm_parameters_tracked', {
        utm_source: utm_source || 'not_set',
        utm_medium: utm_medium || 'not_set',
        utm_campaign: utm_campaign || 'not_set',
        utm_content: utm_content || 'not_set',
        utm_term: utm_term || 'not_set',
      });
    }
  } catch (error) {
    console.error('Failed to track UTM parameters:', error);
  }
};

/**
 * Track first-touch attribution (first interaction with property)
 */
export const trackFirstTouchAttribution = (
  propertyId: string,
  parentAccountId?: string,
  source?: string
) => {
  analyticsManager.trackCustomEvent('first_touch_attribution', {
    property_id: propertyId,
    parent_account_id: parentAccountId,
    source: source || 'direct',
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track last-touch attribution (conversion point)
 */
export const trackLastTouchAttribution = (
  propertyId: string,
  parentAccountId?: string,
  conversionType?: string,
  source?: string
) => {
  analyticsManager.trackCustomEvent('last_touch_attribution', {
    property_id: propertyId,
    parent_account_id: parentAccountId,
    conversion_type: conversionType || 'purchase',
    source: source || 'direct',
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track property view in customer journey
 */
export const trackPropertyViewAttribution = (
  propertyId: string,
  parentAccountId?: string,
  touchPoint: string = 'property_view',
  source?: string
) => {
  analyticsManager.trackCustomEvent('property_view_attribution', {
    property_id: propertyId,
    parent_account_id: parentAccountId,
    touch_point: touchPoint,
    source: source || 'direct',
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track attribution touch point
 */
export const trackAttributionTouchPoint = (
  propertyId: string,
  touchPointType: string,
  touchPointValue?: string,
  parentAccountId?: string
) => {
  analyticsManager.trackCustomEvent('attribution_touch_point', {
    property_id: propertyId,
    parent_account_id: parentAccountId,
    touch_point_type: touchPointType,
    touch_point_value: touchPointValue,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track multi-touch attribution path (complete user journey)
 */
export const trackMultiTouchAttributionPath = (
  propertyId: string,
  touchPoints: string[],
  conversionType?: string,
  parentAccountId?: string
) => {
  analyticsManager.trackCustomEvent('multi_touch_attribution_path', {
    property_id: propertyId,
    parent_account_id: parentAccountId,
    touch_point_count: touchPoints.length,
    touch_points: touchPoints.join('|'),
    conversion_type: conversionType || 'purchase',
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track conversion funnel step
 */
export const trackConversionFunnelStep = (
  funnelName: string,
  stepNumber: number,
  stepName: string,
  propertyId?: string,
  parentAccountId?: string
) => {
  analyticsManager.trackCustomEvent('conversion_funnel_step', {
    funnel_name: funnelName,
    step_number: stepNumber,
    step_name: stepName,
    property_id: propertyId,
    parent_account_id: parentAccountId,
  });
};

/**
 * Track conversion funnel abandonment
 */
export const trackConversionFunnelAbandon = (
  funnelName: string,
  stepNumber: number,
  stepName: string,
  abandonReason?: string,
  propertyId?: string,
  parentAccountId?: string
) => {
  analyticsManager.trackCustomEvent('conversion_funnel_abandoned', {
    funnel_name: funnelName,
    step_number: stepNumber,
    step_name: stepName,
    abandon_reason: abandonReason,
    property_id: propertyId,
    parent_account_id: parentAccountId,
  });
};

/**
 * Track organic search keyword (if available from referrer)
 */
export const trackOrganicSearchKeyword = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const keyword = params.get('q') || params.get('keyword');

    if (keyword) {
      analyticsManager.trackCustomEvent('organic_search_keyword', {
        keyword: keyword,
      });
    }
  } catch (error) {
    console.error('Failed to track organic search keyword:', error);
  }
};

/**
 * Track device/browser details for attribution
 */
export const trackDeviceDetails = () => {
  try {
    const details = {
      device_type: navigator.deviceMemory ? 'high-end' : 'standard',
      platform: navigator.platform,
      user_agent: navigator.userAgent.substring(0, 100),
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
    };

    analyticsManager.trackCustomEvent('device_details_tracked', details);
  } catch (error) {
    console.error('Failed to track device details:', error);
  }
};
